<script setup lang="ts">
import {computed, ref, watch, onBeforeMount} from 'vue';
import type {QuestionMultiChoice} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {QuasarOption} from '@/ts/types/component/select-multichoice';
import {useRequiredSelectRule} from '@/ts/composables/pure/use-input-rules';
import BadgePill from '@/vue/atoms/badge-pill.vue';

const props = defineProps<{
  question: QuestionMultiChoice;
}>();

defineEmits<{
  readyToGoNext: [];
}>();

const model = defineModel<UuidDTO[]>();
const selectedValue = ref<UuidDTO | null>(null);
const requiredRule = useRequiredSelectRule();

// Only apply required rule if minOptions >= 1
const rules = computed(() => {
  return props.question.multiChoice.minOptions >= 1 ? [requiredRule] : [];
});

const options = computed<QuasarOption<UuidDTO>[]>(() => props.question.multiChoice.options.map(opt => ({
  label: opt.text,
  value: opt.id,
})));

onBeforeMount(() => {
  if (model.value && model.value.length > 0) {
    [selectedValue.value] = model.value;
  } else if (props.question.multiChoiceSelectedIds && props.question.multiChoiceSelectedIds.length > 0) {
    // Pre-populate from previously answered questionnaire
    [selectedValue.value] = props.question.multiChoiceSelectedIds;
  }
});

watch(selectedValue, (newVal) => {
  model.value = newVal ? [newVal] : [];
});

const questionLabel = computed(() => {
  const div = document.createElement('div');
  div.innerHTML = props.question.name;
  return div.textContent ?? div.innerText ?? '';
});
</script>

<template>
  <div class="question-dropdown">
    <badge-pill theme="blue" :label="$t('question')"/>
    <div v-safe-html="question.name" class="question text-h6"/>

    <q-select
      v-model="selectedValue"
      :options="options"
      :label="questionLabel"
      :aria-label="questionLabel"
      :rules="rules"
      emit-value
      map-options
      class="dropdown-select"
      outlined
      behavior="menu"
      popup-content-class="dropdown-select-popup"
    />
  </div>
</template>

<style scoped lang="scss">
.question-dropdown {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.question {
  margin: var(--Spacing-spacing-2) 0;
  text-align: center;
  max-width: 700px;
  @include font-display-3;
}

.dropdown-select {
  min-width: 250px;
  max-width: 400px;
}

/* style the dropdown options popup */
:deep(.dropdown-select-popup) {
  /* small window instead of taking all screen height */
  max-height: 220px; /* tweak as you like */
  overflow-y: auto;

  /* make list width match the select component */
  min-width: 100%;
  max-width: 100%;
  width: 100%;
}
</style>
